import { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo, ReactNode } from "react";
import { logger } from "@/utils/logger";
import { extractUserInfo, decodeJWT } from "@/utils/jwtDecoder";
import { isLocalDevBypass, DEV_USER_INFO, createDevToken, isDevModeToken } from "@/utils/devMode";
import type { UserInfo } from "@/types/auth";

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'no-access' | 'tos-required';

interface AuthContextType {
  aiewToken: string | null;
  authStatus: AuthStatus;
  isLoading: boolean;
  userInfo: UserInfo | null;
  login: () => void;
  logout: () => void;
  refreshToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [aiewToken, setAiewToken] = useState<string | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
  const [isLoading, setIsLoading] = useState(true);
  const tokenRefreshTimer = useRef<number | null>(null);
  const preExpiryRefreshTimer = useRef<number | null>(null);
  const lastRefreshAttempt = useRef<number>(0);

  // Get AIEW URL from environment variable (defaults to production if not set)
  const AIEW_BASE_URL = import.meta.env.VITE_AIEW_URL || "https://ai-edge.io";
  const AIEW_TOKEN_ENDPOINT = `${AIEW_BASE_URL}/api/tokens/optimizer`;

  const login = useCallback(() => {
    const redirectUrl = window.location.origin;
    window.location.href = `${AIEW_BASE_URL}/login?redirect=${encodeURIComponent(redirectUrl)}`;
  }, [AIEW_BASE_URL]);

  const logout = useCallback(() => {
    setAiewToken(null);
    if (tokenRefreshTimer.current) clearInterval(tokenRefreshTimer.current);
    if (preExpiryRefreshTimer.current) clearTimeout(preExpiryRefreshTimer.current);
    const redirectUrl = window.location.origin;
    window.location.href = `${AIEW_BASE_URL}/login?redirect=${encodeURIComponent(redirectUrl)}`;
  }, [AIEW_BASE_URL]);

  /**
   * Check if token indicates ToS has been accepted
   * Returns true if accepted, false if not accepted or claim is missing
   */
  const checkTosAcceptance = useCallback((token: string): boolean => {
    // Dev mode bypass - assume ToS accepted in local development
    if (isLocalDevBypass() && isDevModeToken(token)) {
      return true;
    }

    const payload = decodeJWT(token);
    // Treat missing claim as false (old tokens, backward compatibility)
    // Note: AIE uses snake_case for this claim (has_accepted_current_tos)
    return payload?.has_accepted_current_tos === true;
  }, []);

  /**
   * Redirect user to AIE ToS acceptance page
   */
  const redirectToTosAcceptance = useCallback(() => {
    setAuthStatus('tos-required');
    setAiewToken(null);
    if (tokenRefreshTimer.current) clearInterval(tokenRefreshTimer.current);
    if (preExpiryRefreshTimer.current) clearTimeout(preExpiryRefreshTimer.current);
    logger.info("User has not accepted ToS, redirecting to AI Edge ToS page");
    window.location.href = `${AIEW_BASE_URL}/accept-tos?service=optimizer`;
  }, [AIEW_BASE_URL]);

  // Extract user info from token whenever it changes (memoized to prevent unnecessary re-renders)
  const userInfo = useMemo(() => {
    if (!aiewToken) {
      return null;
    }

    // Development bypass: Return mock user info
    if (isLocalDevBypass() && isDevModeToken(aiewToken)) {
      logger.debug("🔧 [DEV MODE] Using mock user info:", DEV_USER_INFO);
      return DEV_USER_INFO as UserInfo;
    }

    try {
      const info = extractUserInfo(aiewToken);
      logger.debug("Extracted user info from AIEW token:", info as unknown as Record<string, unknown>);
      return info;
    } catch (error) {
      logger.error("Failed to extract user info from token:", error);
      return null;
    }
  }, [aiewToken]);

  const fetchTokenWithRetry = useCallback(async (attempt = 1): Promise<string | null> => {
    try {
      const response = await fetch(AIEW_TOKEN_ENDPOINT, {
        method: 'POST',
        credentials: 'include', // Sends the ai-edge.io session cookie
      });

      if (response.ok) {
        const { accessToken } = await response.json();
        if (!accessToken) throw new Error("No accessToken in 200 OK response");
        return accessToken;
      }

      // 403 = Logged in to AIEW but no optimizer entitlement
      // Don't retry - this won't change without manual entitlement grant
      if (response.status === 403) {
        logger.error("User is logged into AIEW but lacks 'optimizer' entitlement.");
        throw new Error("NO_ACCESS"); // Special error to break retry loop
      }

      // 401 = Not logged in at all - retry with backoff for transient issues
      if (response.status === 401) {
        if (attempt < 3) {
          const delay = 200 * attempt; // 200ms, 400ms
          logger.warn(`Token fetch failed (401), retrying in ${delay}ms...`);
          await new Promise(r => setTimeout(r, delay));
          return fetchTokenWithRetry(attempt + 1);
        }
        logger.error("User is not logged into AIEW after retries.");
        return null; // Triggers logout → redirect to login
      }

      throw new Error(`Failed to fetch token: ${response.status}`);

    } catch (error) {
      // Re-throw NO_ACCESS to be handled by caller
      if (error instanceof Error && error.message === "NO_ACCESS") {
        throw error;
      }
      logger.error("Failed to fetch AIEW token", error);
      return null;
    }
  }, [AIEW_TOKEN_ENDPOINT]);

  const refreshToken = useCallback(async () => {
    try {
      const newToken = await fetchTokenWithRetry();
      if (newToken) {
        // Check ToS acceptance on refresh (in case ToS version changed)
        if (!checkTosAcceptance(newToken)) {
          redirectToTosAcceptance();
          return null;
        }
        setAiewToken(newToken);
        setAuthStatus('authenticated');
        return newToken;
      } else {
        // 401 after retries - user needs to log in
        logout();
        return null;
      }
    } catch (error) {
      if (error instanceof Error && error.message === "NO_ACCESS") {
        // 403 - logged in but no entitlement
        // Redirect to AI Edge's professional no-access page
        setAuthStatus('no-access');
        setAiewToken(null);
        if (tokenRefreshTimer.current) clearInterval(tokenRefreshTimer.current);
        if (preExpiryRefreshTimer.current) clearTimeout(preExpiryRefreshTimer.current);
        logger.info("User lacks optimizer entitlement, redirecting to AI Edge no-access page");
        window.location.href = `${AIEW_BASE_URL}/optimizer/no-access`;
        return null;
      } else {
        // Other errors - logout and redirect
        logout();
        return null;
      }
    }
  }, [fetchTokenWithRetry, logout, AIEW_BASE_URL, checkTosAcceptance, redirectToTosAcceptance]);

  /**
   * Parse token expiry and check if it's close to expiring
   */
  const getTokenExpiry = useCallback((token: string): number | null => {
    if (isLocalDevBypass() && isDevModeToken(token)) {
      // Dev tokens don't have real expiry
      return null;
    }

    const payload = decodeJWT(token);
    if (!payload || !payload.exp) {
      logger.warn("Token missing expiry claim");
      return null;
    }

    return payload.exp; // Returns expiry in seconds (Unix timestamp)
  }, []);

  /**
   * Check if token is close to expiring (within threshold in seconds)
   */
  const isTokenCloseToExpiry = useCallback((token: string, thresholdSeconds: number): boolean => {
    const exp = getTokenExpiry(token);
    if (!exp) return false;

    const nowSeconds = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = exp - nowSeconds;

    return timeUntilExpiry <= thresholdSeconds;
  }, [getTokenExpiry]);

  // Ref to hold the latest token for event handlers (avoids re-running effects)
  const aiewTokenRef = useRef<string | null>(null);
  
  // Keep ref in sync with state
  useEffect(() => {
    aiewTokenRef.current = aiewToken;
  }, [aiewToken]);

  /**
   * Check and refresh token if it's close to expiring
   * Used by visibility change and focus event handlers
   * Uses ref to avoid being recreated when token changes
   */
  const checkAndRefreshToken = useCallback(async () => {
    const currentToken = aiewTokenRef.current;
    if (!currentToken) return;

    // Skip if in dev mode
    if (isLocalDevBypass() && isDevModeToken(currentToken)) {
      return;
    }

    // Refresh if token expires within 5 minutes (300 seconds)
    if (isTokenCloseToExpiry(currentToken, 300)) {
      logger.debug("Token expires soon, refreshing due to visibility/focus event");
      await refreshToken();
    }
  }, [isTokenCloseToExpiry, refreshToken]);

  const scheduleTokenRefresh = useCallback((token: string | null) => {
    // Clear existing timers
    if (tokenRefreshTimer.current) clearInterval(tokenRefreshTimer.current);
    if (preExpiryRefreshTimer.current) clearTimeout(preExpiryRefreshTimer.current);

    if (!token) return;

    // Skip scheduling for dev mode tokens
    if (isLocalDevBypass() && isDevModeToken(token)) {
      return;
    }

    // Schedule pre-expiry refresh based on token's exp claim
    const exp = getTokenExpiry(token);
    if (exp) {
      const nowSeconds = Math.floor(Date.now() / 1000);
      const refreshAtSeconds = exp - 120; // 2 minutes before expiry
      const delayMs = (refreshAtSeconds - nowSeconds) * 1000;

      if (delayMs > 0) {
        logger.debug(`Scheduling pre-expiry token refresh in ${Math.floor(delayMs / 1000)} seconds`);
        preExpiryRefreshTimer.current = window.setTimeout(async () => {
          logger.debug("Pre-expiry token refresh triggered");
          const newToken = await refreshToken();
          if (newToken) {
            // Recursively schedule next refresh
            scheduleTokenRefresh(newToken);
          }
        }, delayMs);
      } else {
        // Prevent infinite loops: if we refreshed less than 10 seconds ago, don't refresh again immediately
        // This handles cases where the server returns an expired token or client clock is skewed
        const now = Date.now();
        const timeSinceLastRefresh = now - (lastRefreshAttempt.current || 0);
        
        if (timeSinceLastRefresh < 10000) {
          logger.error("🚨 Token refresh loop detected! New token is also expired. Logging out to prevent loop.");
          logout();
          return;
        }

        logger.warn("Token already expired or will expire soon, refreshing immediately");
        lastRefreshAttempt.current = now;
        
        refreshToken().then(newToken => {
          if (newToken) {
            scheduleTokenRefresh(newToken);
          }
        });
      }
    }

    // Keep the interval-based refresh as a fallback (every 8 minutes for a 10-min token)
    // This ensures we still refresh even if the scheduled timeout somehow fails
    tokenRefreshTimer.current = window.setInterval(() => {
      logger.debug("Interval-based token refresh (fallback)");
      refreshToken().then(newToken => {
        if (newToken) {
          scheduleTokenRefresh(newToken);
        }
      });
    }, 8 * 60 * 1000);
  }, [getTokenExpiry, refreshToken]);

  // Fetch token on load
  useEffect(() => {
    setIsLoading(true);

    const initializeAuth = async () => {
      // Development bypass: Skip token fetch when running locally without AIEW
      if (isLocalDevBypass()) {
        logger.info("🔧 [DEV MODE] Using local development auth bypass");
        // Use a properly formatted JWT-like placeholder for dev mode
        // The backend will use its own dev fallback when suspension enforcement is off
        const devToken = createDevToken();

        setAiewToken(devToken);
        setAuthStatus('authenticated');
        setIsLoading(false);
        return;
      }

      try {
        const token = await fetchTokenWithRetry();
        if (token) {
          // Check ToS acceptance before allowing access
          if (!checkTosAcceptance(token)) {
            redirectToTosAcceptance();
            return;
          }
          setAiewToken(token);
          setAuthStatus('authenticated');
          scheduleTokenRefresh(token);
        } else {
          // 401 after retries - user not logged in
          setAiewToken(null);
          setAuthStatus('unauthenticated');
        }
      } catch (error) {
        if (error instanceof Error && error.message === "NO_ACCESS") {
          // 403 - logged in to AIEW but no optimizer entitlement
          // Redirect to AI Edge's professional no-access page
          setAiewToken(null);
          setAuthStatus('no-access');
          logger.info("User lacks optimizer entitlement, redirecting to AI Edge no-access page");
          window.location.href = `${AIEW_BASE_URL}/optimizer/no-access`;
        } else {
          // Other errors - treat as unauthenticated
          setAiewToken(null);
          setAuthStatus('unauthenticated');
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Set up visibility change listener for proactive refresh
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        logger.debug("Tab became visible, checking token expiry");
        checkAndRefreshToken();
      }
    };

    // Set up focus listener for proactive refresh
    const handleFocus = () => {
      logger.debug("Window gained focus, checking token expiry");
      checkAndRefreshToken();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Cleanup timers and event listeners on unmount
    return () => {
      if (tokenRefreshTimer.current) clearInterval(tokenRefreshTimer.current);
      if (preExpiryRefreshTimer.current) clearTimeout(preExpiryRefreshTimer.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  // Note: checkAndRefreshToken uses aiewTokenRef internally, so it doesn't need to be
  // in the dependency array. Including it would cause infinite re-renders because
  // it depends on isTokenCloseToExpiry and refreshToken which could change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTokenWithRetry, scheduleTokenRefresh, AIEW_BASE_URL, checkTosAcceptance, redirectToTosAcceptance]);

  const value: AuthContextType = {
    aiewToken,
    authStatus,
    isLoading,
    userInfo,
    login,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
